/**
 * sl-yphr: ELSTER Canvas Block Component (Edgeless View)
 *
 * Renders the ELSTER block on the canvas/edgeless surface.
 * Two-column layout: Eingabe (left) + Status (right).
 * Follows the Note-Reduce Pattern for correct zoom/scale positioning.
 */

import { EDGELESS_BLOCK_CHILD_PADDING } from '@blocksuite/affine-shared/consts';
import {
  getTypeColor,
  normalizeTypeLabel,
} from '@blocksuite/affine-shared/utils';
import { Bound } from '@blocksuite/global/gfx';
import {
  ArrowDownBigIcon,
  DatabaseTableViewIcon,
  LinkedPageIcon,
  SettingsIcon,
} from '@blocksuite/icons/lit';
import {
  getGfxBlockElementModelSize,
  toGfxBlockComponent,
} from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { svg } from 'lit';
import { html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

// sl-7hp9: Inline ELSTER asterisk logo (white) for canvas block header
const ElsterLogoIcon = () => svg`
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em">
    <g transform="translate(12, 12)">
      <rect x="-2.5" y="-10" width="5" height="20" rx="2.5" fill="currentColor" />
      <rect x="-2.5" y="-10" width="5" height="20" rx="2.5" fill="currentColor" transform="rotate(60)" />
      <rect x="-2.5" y="-10" width="5" height="20" rx="2.5" fill="currentColor" transform="rotate(120)" />
    </g>
  </svg>
`;

import { canPersistAutoMeasuredBound } from '../canvas-bound-persistence.js';
import {
  DEFAULT_ELSTER_HEIGHT,
  DEFAULT_ELSTER_WIDTH,
  ElsterBlockSchema,
} from '../schemas/index.js';
import { ElsterBlockComponent } from './elster-block.js';
import * as styles from './elster-canvas-block.css.js';

// Database bridge for runtime name and column lookup
interface ColumnInfo {
  id: string;
  name: string;
  type: string;
}
interface DatabaseBridge {
  findDatabaseByBlockId?: (
    blockId: string
  ) => { databaseTitle: string; docId: string } | undefined;
  getColumnsForDatabase?: (blockId: string) => ColumnInfo[] | null;
}
declare const window: Window & { __databaseBridge?: DatabaseBridge };

export const AFFINE_ELSTER_CANVAS = 'affine-elster-canvas';

export class ElsterCanvasBlockComponent extends toGfxBlockComponent(
  ElsterBlockComponent
) {
  override connectedCallback(): void {
    super.connectedCallback();

    const selection = this.gfx.selection;

    this._editing = selection.has(this.model.id) && selection.editing;
    this._disposables.add(
      selection.slots.updated.subscribe(() => {
        if (selection.has(this.model.id) && selection.editing) {
          this._editing = true;
        } else {
          if (this._isEditingTitle) return;
          this._editing = false;
        }
      })
    );

    const titleText = this.model.props.title;
    const titleUpdate = () => this.requestUpdate();
    if (titleText?.yText) {
      titleText.yText.observe(titleUpdate);
      this._disposables.add(() => {
        titleText.yText.unobserve(titleUpdate);
      });
    }

    // Re-render when any prop changes
    this._disposables.add(
      this.model.propsUpdated.subscribe(() => {
        this.requestUpdate();
      })
    );

    // Re-render when database discovery bridge updates
    const onBridgeUpdate = () => this.requestUpdate();
    window.addEventListener('database-discovery-updated', onBridgeUpdate);
    this._disposables.add(() => {
      window.removeEventListener('database-discovery-updated', onBridgeUpdate);
    });

    // Double-click opens ELSTER sidebar
    let dblClickTimer: ReturnType<typeof setTimeout> | null = null;

    const cancelDblClick = () => {
      if (dblClickTimer !== null) {
        clearTimeout(dblClickTimer);
        dblClickTimer = null;
      }
    };

    const onDblClick = (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      cancelDblClick();

      dblClickTimer = setTimeout(() => {
        dblClickTimer = null;
        this._editing = false;
        this.gfx.selection.set({
          elements: [this.model.id],
          editing: false,
        });
        this._handleOpenConfig(e);
      }, 200);
    };

    const onPointerMoveAfterDblClick = (e: PointerEvent) => {
      if (
        dblClickTimer !== null &&
        (Math.abs(e.movementX) > 2 || Math.abs(e.movementY) > 2)
      ) {
        cancelDblClick();
      }
    };

    this.addEventListener('dblclick', onDblClick);
    this.addEventListener('pointermove', onPointerMoveAfterDblClick);
    this._disposables.add(() => {
      cancelDblClick();
      this.removeEventListener('dblclick', onDblClick);
      this.removeEventListener('pointermove', onPointerMoveAfterDblClick);
    });
  }

  override firstUpdated() {
    const container = this.renderRoot.querySelector(
      '.elster-canvas-content'
    ) as HTMLElement | null;
    if (!container) return;

    const updateHeight = () => {
      if (this.model.store.readonly) return;
      const { height } = getGfxBlockElementModelSize(container);
      if (!height) return;

      const contentHeight = height + 2 * EDGELESS_BLOCK_CHILD_PADDING;

      const bound = Bound.deserialize(this.model.props.xywh);
      if (
        Math.abs(bound.h - contentHeight) > 1 &&
        canPersistAutoMeasuredBound(this.model.props.xywh, {
          x: 0,
          y: 0,
          w: DEFAULT_ELSTER_WIDTH,
          h: DEFAULT_ELSTER_HEIGHT,
        })
      ) {
        bound.h = contentHeight;
        this.model.props.xywh = bound.serialize();
      }
    };

    requestAnimationFrame(updateHeight);

    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateHeight);
    });
    observer.observe(container, { childList: true, subtree: true });
    this._disposables.add(() => observer.disconnect());
  }

  override getRenderingRect() {
    return super.getRenderingRect();
  }

  override renderGfxBlock() {
    const style = {
      borderRadius: '8px',
    };

    return html`
      <div
        class=${styles.elsterContainer}
        style=${styleMap(style)}
        data-editing=${this._editing}
      >
        <elster-canvas-background
          .editing=${this._editing}
          .elster=${this.model}
        ></elster-canvas-background>

        <div
          class=${styles.clipContainer}
          style=${styleMap({ overflow: 'clip' })}
        >
          <div contenteditable="false" class="elster-canvas-content">
            ${this.renderPageContent()}
          </div>
        </div>
      </div>
    `;
  }

  override renderPageContent() {
    const { model } = this;
    const { inputTableId, inputTableName, selectedRowIndex } = model.props;

    const currentDocId = this.model.store?.id;
    const bridge = window.__databaseBridge;

    // Resolve input table
    const inputDbInfo = inputTableId
      ? bridge?.findDatabaseByBlockId?.(inputTableId)
      : undefined;
    const resolvedInputName =
      inputDbInfo?.databaseTitle ?? inputTableName ?? null;
    const isInputCrossDoc = inputDbInfo
      ? inputDbInfo.docId !== currentDocId
      : false;

    const rawTitle = model.props.title.toString();
    const displayTitle = rawTitle || 'UStVA';
    const hasCustomTitle = rawTitle && rawTitle !== 'UStVA';

    return html`
      <div class=${styles.content}>
        <!-- Row 1: Controls -->
        <div class=${styles.controlsRow}>
          <span class=${styles.headerIcon}>${ElsterLogoIcon()}</span>

          <span class=${styles.headerSpacer}></span>

          <button
            class="${styles.actionButton} ${styles.validateButton}"
            @click=${this._handleValidate}
            ?disabled=${!model.hasInput}
            title="Validieren"
          >
            Validieren
          </button>

          <button
            class="${styles.actionButton} ${styles.submitButton}"
            @click=${this._handleSubmit}
            ?disabled=${!model.hasInput}
            title="An ELSTER senden"
          >
            Senden
          </button>

          <button
            class=${styles.settingsButton}
            @click=${this._handleOpenConfig}
            title="Einstellungen"
          >
            ${SettingsIcon()}
          </button>
        </div>

        <!-- Row 2: Title -->
        ${this._isEditingTitle
          ? html`
              <input
                class=${styles.summaryTitleInput}
                .value=${this._titleDraft}
                @input=${this.onTitleInput}
                @keydown=${this.onTitleKeydown}
                @blur=${this.onTitleBlur}
                placeholder="UStVA"
              />
            `
          : html`
              <div
                class=${classMap({
                  [styles.summaryTitle]: true,
                  [styles.summaryTitleEmpty]: !hasCustomTitle,
                })}
                title=${displayTitle}
                @click=${this.onTitleClick}
              >
                ${displayTitle}
              </div>
            `}

        <div class=${styles.divider}></div>

        <!-- Body - Two Columns -->
        <div class=${styles.body}>
          <!-- Input Column (Eingabe) -->
          <div class=${styles.column}>
            <div class=${styles.columnHeader}>
              <span class=${styles.columnIcon}>${ArrowDownBigIcon()}</span>
              Eingabe
            </div>
            ${this._renderInputSection(
              inputTableId,
              resolvedInputName,
              isInputCrossDoc
            )}
          </div>

          <!-- Status Column -->
          <div class=${styles.column}>
            <div class=${styles.columnHeader}>
              <span class=${styles.columnIcon}>${SettingsIcon()}</span>
              Status
            </div>
            ${this._renderStatusSection(selectedRowIndex)}
          </div>
        </div>
      </div>
    `;
  }

  @state()
  private accessor _editing = false;

  @state()
  private accessor _isEditingTitle = false;

  @state()
  private accessor _titleDraft = '';

  // ===========================================================================
  // Title Editing
  // ===========================================================================

  private readonly onTitleClick = (event: MouseEvent) => {
    if (this.model.isLocked()) return;
    if (event.detail > 1) return;
    this._isEditingTitle = true;
    this._titleDraft = this.model.props.title.toString();
    this.updateComplete
      .then(() => {
        const input = this.renderRoot.querySelector(
          `.${styles.summaryTitleInput}`
        ) as HTMLInputElement | null;
        input?.focus();
        input?.select();
      })
      .catch(console.error);
  };

  private readonly onTitleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this._titleDraft = target.value;
  };

  private readonly onTitleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitTitle();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this._isEditingTitle = false;
      this._titleDraft = this.model.props.title.toString();
    }
  };

  private readonly onTitleBlur = () => {
    this.commitTitle();
  };

  private commitTitle() {
    if (!this._isEditingTitle) return;
    if (!this.model.store?.getBlock(this.model.id)) {
      this._isEditingTitle = false;
      return;
    }
    const nextTitle = this._titleDraft.trim();
    const titleText = this.model.props.title;
    titleText.replace(0, titleText.length, nextTitle);
    this._isEditingTitle = false;
  }

  // ===========================================================================
  // Render Helpers
  // ===========================================================================

  private _renderInputSection(
    inputTableId: string | null,
    resolvedName: string | null,
    isCrossDoc: boolean
  ) {
    if (!inputTableId) {
      return html`<div class=${styles.emptyState}>Nicht konfiguriert</div>`;
    }

    const bridge = window.__databaseBridge;
    const allColumns = bridge?.getColumnsForDatabase?.(inputTableId) ?? [];

    return html`
      <div class=${styles.tableCard}>
        <div class=${styles.tableName}>
          <span class=${styles.tableIcon}>${DatabaseTableViewIcon()}</span>
          ${resolvedName || 'Eingabe-Tabelle'}
          ${isCrossDoc
            ? html`<span class=${styles.crossDocIcon}
                >${LinkedPageIcon({ width: '10', height: '10' })}</span
              >`
            : nothing}
        </div>
        ${allColumns.length > 0
          ? html`
              <ul class=${styles.columnList}>
                ${allColumns.slice(0, 5).map(
                  col => html`
                    <li class=${styles.columnItem}>
                      <span class=${styles.columnName}>${col.name}</span>
                      <span
                        class=${styles.typeChip}
                        style=${styleMap({
                          backgroundColor: getTypeColor(col.type),
                        })}
                      >
                        ${normalizeTypeLabel(col.type)}
                      </span>
                    </li>
                  `
                )}
                ${allColumns.length > 5
                  ? html`<li class=${styles.moreHint}>
                      +${allColumns.length - 5} weitere
                    </li>`
                  : nothing}
              </ul>
            `
          : html`<div class=${styles.emptyHint}>Keine Spalten</div>`}
      </div>
    `;
  }

  private _renderStatusSection(selectedRowIndex: number | null) {
    return html`
      <div class=${styles.infoCard}>
        <div class=${styles.infoRow}>
          <span class=${styles.infoLabel}>Zeitraum</span>
          <span class=${styles.infoValue}>
            ${selectedRowIndex !== null
              ? `Zeile ${selectedRowIndex + 1}`
              : '\u2014'}
          </span>
        </div>
        <div class=${styles.infoRow}>
          <span class=${styles.infoLabel}>Details</span>
          <span class=${styles.infoValue}>Fullscreen-Ansicht</span>
        </div>
      </div>
    `;
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  private readonly _handleOpenConfig = (e: Event) => {
    e.stopPropagation();

    window.dispatchEvent(
      new CustomEvent('elster-fullscreen', {
        detail: { blockId: this.model.id },
        bubbles: true,
      })
    );
  };

  private readonly _handleValidate = (e: Event) => {
    e.stopPropagation();

    window.dispatchEvent(
      new CustomEvent('elster-validate', {
        detail: {
          blockId: this.model.id,
          docId: this.model.store?.id,
        },
        bubbles: true,
      })
    );
  };

  private readonly _handleSubmit = (e: Event) => {
    e.stopPropagation();

    window.dispatchEvent(
      new CustomEvent('elster-submit', {
        detail: {
          blockId: this.model.id,
          docId: this.model.store?.id,
        },
        bubbles: true,
      })
    );
  };
}

// =============================================================================
// Interaction Extension
// =============================================================================

export const ElsterBlockInteraction =
  GfxViewInteractionExtension<ElsterCanvasBlockComponent>(
    ElsterBlockSchema.model.flavour,
    {
      resizeConstraint: {
        allowedHandlers: [],
      },

      handleRotate: () => ({
        beforeRotate(context) {
          context.set({ rotatable: false });
        },
      }),

      handleSelection: ({ gfx, view, model }) => ({
        onSelect(context) {
          const { selected, multiSelect } = context;
          const { editing } = gfx.selection;
          const alreadySelected = gfx.selection.has(model.id);

          if (!multiSelect && selected && (alreadySelected || editing)) {
            if (model.isLocked()) return;

            if (alreadySelected && editing) {
              return;
            }

            gfx.selection.set({
              elements: [model.id],
              editing: true,
            });

            view.updateComplete
              .then(() => {
                if (!view.isConnected) {
                  return;
                }
              })
              .catch(console.error);
          } else {
            context.default(context);
          }
        },
      }),
    }
  );
